import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatNumber } from '../lib/format';
import { codeText, formatWorkDate, type EvaluationDetail, type Overview } from '../lib/ai-points';
import { FormPage } from '../components/FormPage';
import { Badge, Checkbox, Detail, Field, Textarea, useToast } from '../components/ui';
import { RunBadges, StatusBadge } from './AiPoints';

const BASE = '/ai-daily-points';

function useEvaluation(id: string) {
  return useQuery({ queryKey: ['ai-points', 'detail', id], queryFn: () => api.get<EvaluationDetail>(`${BASE}/evaluations/${id}`) });
}

/** Who, which day, what was proposed — above every form about one evaluation. */
function EvaluationSummary({ ev }: { ev: EvaluationDetail }) {
  const { t, locale, localized } = useI18n();
  return (
    <dl className="divide-y divide-[var(--line)]">
      <Detail label={t('common.employee')}>{localized(ev.employee)}</Detail>
      <Detail label={t('aiPoints.col.date')}>{formatWorkDate(ev.workDate, locale)}</Detail>
      <Detail label={t('common.status')}>
        <StatusBadge status={ev.status} />
      </Detail>
      <Detail label={t('aiPoints.proposed')}>{ev.score != null ? `${formatNumber(ev.score, locale)} / 10` : '—'}</Detail>
    </dl>
  );
}

/**
 * Route: `/ai-points/:id/reject` — reject one proposal, with an optional note the administrators keep.
 * Nothing is credited and the employee is never told (CR §2.8).
 */
export function AiPointsRejectPage() {
  const { id = '' } = useParams();
  const { t, localized } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const { data: ev, isLoading, error, refetch } = useEvaluation(id);
  const reject = useMutation({
    mutationFn: () => api.post<{ replayed: boolean }>(`${BASE}/evaluations/${id}/reject`, { revision: ev?.revision, note: note.trim() || undefined }),
    onSuccess: (r) => {
      toast.success(r.replayed ? t('aiPoints.alreadyRejected') : t('aiPoints.rejectedToast', { name: ev ? localized(ev.employee) : '' }));
      void queryClient.invalidateQueries({ queryKey: ['ai-points'] });
      navigate(ev ? `/ai-points?date=${ev.workDate}` : '/ai-points');
    },
    onError: (e: Error) => {
      toast.error(e instanceof ApiError && e.code === 'AI_POINTS_PROPOSAL_CHANGED' ? t('aiPoints.changed') : e.message);
      void refetch();
    },
  });
  return (
    <FormPage
      title={t('aiPoints.rejectTitle')}
      backTo={`/ai-points/${id}`}
      backLabel={t('aiPoints.backToProposal')}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      summary={ev && <EvaluationSummary ev={ev} />}
      submitLabel={t('aiPoints.reject')}
      onSubmit={() => reject.mutate()}
      submitting={reject.isPending}
      disabled={!ev?.can.reject}
      destructive
    >
      <Field label={t('aiPoints.rejectNoteLabel')} hint={t('aiPoints.rejectNoteHint')}>
        <Textarea value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} dir="auto" autoFocus />
      </Field>
      {ev && !ev.can.reject && <p className="mt-3 text-body-sm text-muted">{ev.selfNotice ? t(`aiPoints.selfLong.${ev.selfNotice}` as TranslationKey) : t('aiPoints.notRejectable')}</p>}
    </FormPage>
  );
}

/**
 * Route: `/ai-points/:id/retry` — read one day again and send it back to the model: deferred, failed,
 * blocked, not eligible, or an unapproved proposal (Regenerate). A new revision; the old one stays in
 * the day's history. A reason is kept with it.
 */
export function AiPointsRetryPage() {
  const { id = '' } = useParams();
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const { data: ev, isLoading, error, refetch } = useEvaluation(id);
  const retry = useMutation({
    mutationFn: () => api.post<{ moved: Record<string, number> }>(`${BASE}/evaluations/${id}/retry`, { reason: reason.trim() }),
    onSuccess: () => {
      toast.success(t('aiPoints.retriedToast'));
      void queryClient.invalidateQueries({ queryKey: ['ai-points'] });
      navigate(`/ai-points/${id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const regenerate = ev?.status === 'PENDING_ADMIN_APPROVAL';
  return (
    <FormPage
      title={t(regenerate ? 'aiPoints.regenerateTitle' : 'aiPoints.retryTitle')}
      subtitle={t('aiPoints.retrySubtitle')}
      backTo={`/ai-points/${id}`}
      backLabel={t('aiPoints.backToProposal')}
      loading={isLoading}
      error={error}
      onRetry={() => void refetch()}
      summary={
        ev && (
          <>
            <EvaluationSummary ev={ev} />
            {ev.statusReason && <p className="mt-2 text-body-sm text-muted">{codeText(t, 'aiPoints.reason', ev.statusReason)}</p>}
          </>
        )
      }
      submitLabel={t(regenerate ? 'aiPoints.regenerate' : 'aiPoints.retry')}
      onSubmit={() => retry.mutate()}
      submitting={retry.isPending}
      disabled={!ev?.can.retry || reason.trim().length < 3}
    >
      <Field label={t('aiPoints.retryReason')} required hint={t('aiPoints.retryReasonHint')}>
        <Textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} dir="auto" autoFocus />
      </Field>
    </FormPage>
  );
}

/**
 * Route: `/ai-points/runs/:id/retry` — a run's deferred, failed, blocked and not-eligible days read again
 * and sent back to the model; with `?pending=1`, its unapproved proposals regenerated too. Never a
 * decided one, never the reviewer's own.
 */
export function AiPointsRunRetryPage() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const [includePending, setIncludePending] = useState(params.get('pending') === '1');
  const overview = useQuery({ queryKey: ['ai-points', 'overview'], queryFn: () => api.get<Overview>(`${BASE}/overview`) });
  const run = overview.data?.runs.find((r) => r.id === id) ?? null;
  const retry = useMutation({
    mutationFn: () => api.post<{ moved: Record<string, number> }>(`${BASE}/runs/${id}/retry`, { reason: reason.trim(), includePending }),
    onSuccess: (r) => {
      const n = Object.values(r.moved).reduce((a, b) => a + b, 0);
      toast.success(t('aiPoints.runRetriedToast', { n: formatNumber(n, locale) }));
      void queryClient.invalidateQueries({ queryKey: ['ai-points'] });
      navigate(run ? `/ai-points?date=${run.workDate}` : '/ai-points');
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const c = run?.counts ?? {};
  return (
    <FormPage
      title={t('aiPoints.retryRunTitle')}
      subtitle={t('aiPoints.retrySubtitle')}
      backTo={run ? `/ai-points?date=${run.workDate}` : '/ai-points'}
      backLabel={t('aiPoints.title')}
      loading={overview.isLoading}
      error={overview.error}
      onRetry={() => void overview.refetch()}
      summary={
        run && (
          <dl className="divide-y divide-[var(--line)]">
            <Detail label={t('aiPoints.col.date')}>
              <span className="inline-flex items-center gap-2">
                {formatWorkDate(run.workDate, locale)}
                <RunBadges run={run} />
              </span>
            </Detail>
            {(['DEFERRED', 'FAILED_AI', 'BLOCKED_CONFIGURATION', 'NOT_ELIGIBLE', 'PENDING_ADMIN_APPROVAL'] as const).map((s) => (
              <Detail key={s} label={t(`aiPoints.status.${s}` as TranslationKey)}>
                <Badge tone="neutral">{formatNumber(c[s] ?? 0, locale)}</Badge>
              </Detail>
            ))}
          </dl>
        )
      }
      submitLabel={t('aiPoints.retry')}
      onSubmit={() => retry.mutate()}
      submitting={retry.isPending}
      disabled={!run || reason.trim().length < 3}
    >
      <Field label={t('aiPoints.retryReason')} required hint={t('aiPoints.retryReasonHint')}>
        <Textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} dir="auto" autoFocus />
      </Field>
      <div className="mt-4">
        <Checkbox checked={includePending} onChange={(e) => setIncludePending(e.target.checked)} label={t('aiPoints.includePending')} />
        <p className="mt-1 text-caption text-faint">{t('aiPoints.includePendingHint')}</p>
      </div>
    </FormPage>
  );
}
